import { ArrowRight, CheckCircle2, FileText, Link2, ShieldCheck } from "lucide-react";
import { track } from "./analytics";
import type { Locale } from "./i18n";
import { localizedPath } from "./route";
import {
  getThreadsCompatibilityContent,
  isThreadsCompatibilityLocale,
  threadsCompatibilityPublishedAt,
  threadsCompatibilityUpdatedAt,
  type CompatibilityRow
} from "./threadsCompatibility";

export function ThreadsCompatibilityPage({ locale }: { locale: Locale }) {
  const copy = getThreadsCompatibilityContent(locale);
  return (
    <main className="guide-main compatibility-main">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <a href={localizedPath(locale, "home")}>SiloSave</a>
        <span>/</span>
        <a href={localizedPath(locale, "downloader")}>Threads</a>
        <span>/</span>
        <span>{copy.breadcrumbLabel}</span>
      </nav>

      <article>
        <header className="guide-hero compatibility-hero">
          <div>
            <p className="section-kicker">SiloSave / {copy.eyebrow}</p>
            <h1>{copy.title}</h1>
            <p className="guide-lead">{copy.lead}</p>
            <div className="editorial-meta">
              <strong>{copy.byline}</strong>
              <span>{copy.publishedLabel} <time dateTime={threadsCompatibilityPublishedAt}>{threadsCompatibilityPublishedAt}</time></span>
              <span>{copy.updatedLabel} <time dateTime={threadsCompatibilityUpdatedAt}>{threadsCompatibilityUpdatedAt}</time></span>
            </div>
          </div>
          <CompatibilityVisual labels={copy.visualLabels} />
        </header>

        <section className="guide-answer" aria-labelledby="compatibility-answer-title">
          <p className="section-kicker">Threads / Scope</p>
          <h2 id="compatibility-answer-title">{copy.directTitle}</h2>
          <p>{copy.directAnswer}</p>
        </section>

        <section className="guide-section compatibility-snapshot" aria-labelledby="compatibility-snapshot-title">
          <h2 id="compatibility-snapshot-title">{copy.snapshotTitle}</h2>
          <p>{copy.snapshotLead}</p>
          <div className="compatibility-facts">
            {copy.facts.map((fact) => (
              <article key={fact.label}>
                <strong>{fact.value}</strong>
                <h3>{fact.label}</h3>
                <p>{fact.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <CompatibilityTable id="compatibility-inputs" title={copy.inputTitle} lead={copy.inputLead} columns={copy.inputColumns} rows={copy.inputRows} />
        <CompatibilityTable id="compatibility-outputs" title={copy.outputTitle} lead={copy.outputLead} columns={copy.outputColumns} rows={copy.outputRows} />

        <section className="guide-section guide-limits" aria-labelledby="compatibility-limits-title">
          <h2 id="compatibility-limits-title">{copy.limitsTitle}</h2>
          <ul>{copy.limits.map((limit) => <li key={limit}>{limit}</li>)}</ul>
        </section>

        <section className="guide-section compatibility-method" aria-labelledby="compatibility-method-title">
          <h2 id="compatibility-method-title">{copy.methodTitle}</h2>
          {copy.methodParagraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </section>

        <section className="guide-section compatibility-changes" aria-labelledby="compatibility-changes-title">
          <h2 id="compatibility-changes-title">{copy.changesTitle}</h2>
          <ol>{copy.changes.map((change) => <li key={`${change.date}-${change.text}`}><time dateTime={change.date}>{change.date}</time><p>{change.text}</p></li>)}</ol>
        </section>

        <section className="guide-section source-list" aria-labelledby="compatibility-sources-title">
          <h2 id="compatibility-sources-title">{copy.sourcesTitle}</h2>
          <ul>{copy.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.title}</a><span>{source.publisher}</span></li>)}</ul>
        </section>

        <aside className="guide-cta">
          <div><p className="section-kicker">SiloSave / Threads</p><h2>{copy.ctaTitle}</h2><p>{copy.ctaText}</p></div>
          <a href={localizedPath(locale, "downloader")} onClick={() => track("guide_cta_click", { source_page: "threads_downloader_compatibility", target: "threads_downloader", placement: "final_cta" })}>{copy.ctaLabel}<ArrowRight size={17} /></a>
        </aside>
      </article>
    </main>
  );
}

export function ThreadsEvidencePreview({ locale }: { locale: Locale }) {
  if (!isThreadsCompatibilityLocale(locale)) return null;
  const copy = getThreadsCompatibilityContent(locale);
  return (
    <section id="compatibility" className="section threads-evidence-preview" aria-labelledby="threads-evidence-title">
      <div className="threads-evidence-heading">
        <div>
          <p className="section-kicker">{copy.previewKicker}</p>
          <h2 id="threads-evidence-title">{copy.previewTitle}</h2>
        </div>
        <div>
          <p>{copy.previewLead}</p>
          <ul>{copy.previewFacts.map((fact) => <li key={fact}><CheckCircle2 size={17} aria-hidden="true" />{fact}</li>)}</ul>
          <a href={localizedPath(locale, "threads-downloader-compatibility")} onClick={() => track("guide_cta_click", { source_page: "threads_downloader", target: "threads_downloader_compatibility", placement: "compatibility_preview" })}>{copy.previewLink}<ArrowRight size={17} /></a>
        </div>
      </div>
    </section>
  );
}

function CompatibilityTable({ id, title, lead, columns, rows }: { id: string; title: string; lead: string; columns: readonly [string, string, string, string]; rows: readonly CompatibilityRow[] }) {
  return (
    <section className="guide-section compatibility-table-section" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{title}</h2>
      <p>{lead}</p>
      <div className="guide-table-wrap compatibility-table-wrap">
        <table>
          <caption className="sr-only">{title}</caption>
          <thead><tr>{columns.map((column) => <th key={column} scope="col">{column}</th>)}</tr></thead>
          <tbody>{rows.map((row) => (
            <tr key={`${row.item}-${row.example}`}>
              <th scope="row" data-label={columns[0]}>{row.item}</th>
              <td data-label={columns[1]}><code>{row.example}</code></td>
              <td data-label={columns[2]}><span className={`compatibility-status ${row.tone}`}>{row.status}</span></td>
              <td data-label={columns[3]}>{row.result}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </section>
  );
}

function CompatibilityVisual({ labels }: { labels: readonly [string, string, string] }) {
  return (
    <div className="compatibility-visual" role="img" aria-label={labels.join(" → ")}>
      <div><Link2 aria-hidden="true" /><span>{labels[0]}</span></div>
      <ArrowRight aria-hidden="true" />
      <div><ShieldCheck aria-hidden="true" /><span>{labels[1]}</span></div>
      <ArrowRight aria-hidden="true" />
      <div><FileText aria-hidden="true" /><span>{labels[2]}</span></div>
    </div>
  );
}
